import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { Shield, Smartphone, Mail, Loader2, CheckCircle2, XCircle, Copy, AlertTriangle } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

export default function TwoFactorSettings({ token }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [setupMode, setSetupMode] = useState(null); // null, 'totp', 'email'
  const [totpData, setTotpData] = useState(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [backupCodes, setBackupCodes] = useState(null);
  const [saving, setSaving] = useState(false);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    axios.get(`${BACKEND}/api/auth/2fa/status`, { headers })
      .then(r => setStatus(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [headers]);

  const setupTotp = async () => {
    setSaving(true);
    try {
      const res = await axios.post(`${BACKEND}/api/auth/2fa/setup`, { method: 'totp' }, { headers });
      setTotpData(res.data);
      setSetupMode('totp');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const setupEmail = async () => {
    setSaving(true);
    try {
      await axios.post(`${BACKEND}/api/auth/2fa/setup`, { method: 'email' }, { headers });
      toast.success('Email 2FA enabled!');
      setStatus({ enabled: true, method: 'email' });
      setSetupMode(null);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const verifyTotp = async () => {
    setSaving(true);
    try {
      const res = await axios.post(`${BACKEND}/api/auth/2fa/verify-setup`, { code: verifyCode }, { headers });
      setBackupCodes(res.data.backup_codes);
      setStatus({ enabled: true, method: 'totp', has_backup_codes: true });
      toast.success('Authenticator app verified and enabled!');
    } catch (e) { toast.error(e.response?.data?.detail || 'Invalid code'); }
    finally { setSaving(false); }
  };

  const disable = async () => {
    if (!window.confirm('Disable two-factor authentication? Your account will be less secure.')) return;
    try {
      await axios.post(`${BACKEND}/api/auth/2fa/disable`, {}, { headers });
      setStatus({ enabled: false, method: '' });
      setSetupMode(null);
      setTotpData(null);
      setBackupCodes(null);
      toast.success('2FA disabled');
    } catch { toast.error('Failed'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-6" data-testid="2fa-settings">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>Two-Factor Authentication</h2>
          <p className="text-sm text-muted-foreground">Add an extra layer of security to your account.</p>
        </div>
        {status?.enabled && <Badge className="bg-emerald-100 text-emerald-700 gap-1"><CheckCircle2 className="h-3 w-3" /> Enabled ({status.method})</Badge>}
      </div>

      {/* Current status */}
      {status?.enabled ? (
        <div className="p-4 rounded-xl border bg-emerald-50/50 border-emerald-200">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="h-5 w-5 text-emerald-600" />
            <div>
              <p className="text-sm font-medium text-emerald-800">2FA is active</p>
              <p className="text-xs text-emerald-700/70">Method: {status.method === 'totp' ? 'Authenticator app' : 'Email verification code'}</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={disable} className="mt-3 text-xs text-destructive hover:text-destructive">Disable 2FA</Button>
        </div>
      ) : backupCodes ? (
        /* Show backup codes after TOTP setup */
        <div className="p-4 rounded-xl border bg-amber-50 border-amber-200">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="h-4 w-4 text-amber-700" />
            <h3 className="text-sm font-semibold text-amber-800">Save your backup codes</h3>
          </div>
          <p className="text-xs text-amber-700/80 mb-3">If you lose access to your authenticator app, use one of these codes to sign in. Each code can only be used once. Store them somewhere safe.</p>
          <div className="grid grid-cols-4 gap-2 mb-3">
            {backupCodes.map((code, i) => (
              <code key={i} className="bg-white px-2 py-1.5 rounded border text-xs font-mono text-center">{code}</code>
            ))}
          </div>
          <Button variant="outline" size="sm" className="gap-1 text-xs" onClick={() => { navigator.clipboard.writeText(backupCodes.join('\n')); toast.success('Codes copied'); }}>
            <Copy className="h-3 w-3" /> Copy all codes
          </Button>
          <Button size="sm" className="ml-2 text-xs" onClick={() => setBackupCodes(null)}>Done</Button>
        </div>
      ) : setupMode === 'totp' && totpData ? (
        /* TOTP setup flow */
        <div className="space-y-4">
          <div className="p-4 rounded-xl border bg-card">
            <h3 className="text-sm font-semibold mb-2">1. Scan QR code with your authenticator app</h3>
            <p className="text-xs text-muted-foreground mb-3">Use Google Authenticator, Authy, 1Password, or any TOTP app.</p>
            <div className="flex justify-center mb-3">
              <img src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(totpData.provisioning_uri)}`} alt="TOTP QR Code" className="rounded-lg border" />
            </div>
            <p className="text-[10px] text-muted-foreground text-center">Can't scan? Enter manually: <code className="bg-muted px-1.5 py-0.5 rounded text-[10px] font-mono">{totpData.secret}</code></p>
          </div>
          <div className="p-4 rounded-xl border bg-card">
            <h3 className="text-sm font-semibold mb-2">2. Enter the 6-digit code from your app</h3>
            <div className="flex gap-2">
              <Input value={verifyCode} onChange={e => setVerifyCode(e.target.value)} placeholder="000000" className="font-mono text-lg text-center tracking-[0.3em] max-w-[180px]" maxLength={6} data-testid="totp-verify-code" />
              <Button onClick={verifyTotp} disabled={saving || verifyCode.length < 6}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Verify & Enable'}
              </Button>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={() => { setSetupMode(null); setTotpData(null); }} className="text-xs">Cancel</Button>
        </div>
      ) : (
        /* Method selection */
        <div className="space-y-3">
          <h3 className="text-sm font-semibold">Choose a method</h3>
          <button onClick={setupTotp} disabled={saving}
            className="w-full text-left p-4 rounded-xl border hover:border-blue-400 hover:bg-blue-50/30 transition-all">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center"><Smartphone className="h-5 w-5 text-white" /></div>
              <div>
                <span className="text-sm font-semibold block">Authenticator App</span>
                <span className="text-xs text-muted-foreground">Google Authenticator, Authy, 1Password — most secure</span>
              </div>
              <Badge className="ml-auto text-[9px] bg-emerald-100 text-emerald-700">Recommended</Badge>
            </div>
          </button>
          <button onClick={setupEmail} disabled={saving}
            className="w-full text-left p-4 rounded-xl border hover:border-blue-400 hover:bg-blue-50/30 transition-all">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center"><Mail className="h-5 w-5 text-white" /></div>
              <div>
                <span className="text-sm font-semibold block">Email Verification</span>
                <span className="text-xs text-muted-foreground">Receive a 6-digit code via email on each login</span>
              </div>
            </div>
          </button>
        </div>
      )}
    </div>
  );
}
